import React, { useState, useEffect, useRef } from "react";
import {
  collection,
  getDocs,
  query,
  orderBy,
  limit,
  startAfter,
} from "firebase/firestore";
import { db } from "../register/firebase";
import { Link } from "react-router-dom";
import { PenBox } from "lucide-react";
import BlogEditorButton from "./BlogEditorButton";

function Blogs() {
  const [posts, setPosts] = useState([]);
  const [lastVisible, setLastVisible] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  const isFetchingRef = useRef(false); // prevents overlapping fetches

  useEffect(() => {
    fetchPosts();
  }, []);

  const extractTitle = (html) => {
    const match = html.match(/<h1[^>]*>(.*?)<\/h1>/i);
    return match ? match[1] : "Untitled";
  };

  const fetchPosts = async () => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      const postsRef = collection(db, "posts");
      const baseQuery = query(
        postsRef,
        orderBy("createdAt", "desc"),
        limit(10)
      );

      const snapshot = await getDocs(baseQuery);
      const newPosts = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setPosts(newPosts);
      const lastDoc = snapshot.docs[snapshot.docs.length - 1];
      setLastVisible(lastDoc);
      setHasMore(snapshot.docs.length === 10);
    } catch (error) {
      console.error("Error fetching posts:", error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  const fetchMorePosts = async () => {
    if (!lastVisible || isFetchingRef.current || !hasMore) return;

    isFetchingRef.current = true;
    setIsLoading(true);

    try {
      const postsRef = collection(db, "posts");
      const nextQuery = query(
        postsRef,
        orderBy("createdAt", "desc"),
        startAfter(lastVisible),
        limit(10)
      );

      const snapshot = await getDocs(nextQuery);
      const newPosts = snapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));

      setPosts((prev) => [...prev, ...newPosts]);
      const lastDoc = snapshot.docs[snapshot.docs.length - 1];
      setLastVisible(lastDoc);
      setHasMore(snapshot.docs.length === 10);
    } catch (error) {
      console.error("Error fetching more posts:", error);
    } finally {
      setIsLoading(false);
      isFetchingRef.current = false;
    }
  };

  const filteredPosts = posts.filter((post) =>
    extractTitle(post.content).toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="container mx-auto px-4 py-20">
      <div className="flex justify-between items-center mb-8 md:flex-row flex-col">
        <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mb-8 w-full max-w-6xl mx-auto px-4">
          {/* Search Input */}
          <div className="relative w-full sm:max-w-xl">
            <span className="absolute inset-y-0 left-0 flex items-center pl-4 text-gray-400 pointer-events-none">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M21 21l-4.35-4.35m0 0A7.5 7.5 0 104.5 4.5a7.5 7.5 0 0012.15 12.15z"
                />
              </svg>
            </span>
            <input
              type="text"
              className="w-full p-4 pl-12 pr-4 text-base text-gray-700 placeholder-gray-400 bg-white border border-gray-300 rounded-2xl shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-200"
              placeholder="Search blog by title..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {/* Write Blog Button */}
          <BlogEditorButton />
        </div>
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
        {filteredPosts.map((post) => (
          <Link
            key={post.id}
            to={`/blog/${post.id}`}
            state={{ post }}
            className="group block bg-white rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 hover:border-blue-300 overflow-hidden"
          >
            <div className="flex flex-col justify-between h-[200px] p-4">
              {/* Title */}
              <h2 className="text-base md:text-lg font-semibold text-gray-800 group-hover:text-blue-600 transition-colors duration-200 line-clamp-1">
                {post.title ?? extractTitle(post.content)}
              </h2>

              {/* Blog Preview */}
              <div
                className="text-gray-600 text-sm line-clamp-2 leading-snug"
                dangerouslySetInnerHTML={{ __html: post.content }}
              ></div>

              {/* Read More */}
              <div className="mt-2 text-blue-500 text-xs font-medium inline-flex items-center gap-1 group-hover:underline transition-colors duration-200">
                Read More
                <svg
                  className="w-3.5 h-3.5"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M17 8l4 4m0 0l-4 4m4-4H3"
                  />
                </svg>
              </div>
            </div>
          </Link>
        ))}
      </div>

      {hasMore && (
        <div className="flex justify-center mt-8">
          <button
            onClick={fetchMorePosts}
            disabled={isLoading}
            className={`py-3 px-8 text-lg font-semibold rounded-lg ${
              isLoading
                ? "bg-gray-400 cursor-not-allowed"
                : "bg-blue-600 text-white hover:bg-blue-700"
            } transition duration-300`}
          >
            {isLoading ? "Loading..." : "Load More"}
          </button>
        </div>
      )}
    </div>
  );
}

export default Blogs;
